import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import Breadcrumbs from '../../components/Breadcrumbs';
import { FieldOwnerIcon, RocketIcon, ArrowLeftIcon } from '../../assets/icons';
import OrgNodeTree, { orgNodeLabels, keyId, nodeKey } from '../../components/OrgNodeTree';
import TreeDropdown from '../../components/TreeDropdown';
import ConstituencyTreeSelect from '../../components/ConstituencyTreeSelect';
import { useAppDispatch } from '../../app/hooks';
import {
  useGetMyTreeQuery,
  useGetNodeTreeQuery,
  useGetBoundariesQuery,
} from '../../features/organizations/organizationsApi';
import {
  resolveBoundarySeed,
  EC_LEVEL,
  type TreeNode,
} from '../../features/territory/constituencyTree';
import {
  useGetNodeUsersQuery,
  useGetEngagementQuery,
  useCreateEngagementMutation,
  useUpdateEngagementMutation,
  areaUserName,
  engArea,
  engName,
  type Engagement,
  type CreateEngagementPayload,
} from '../../features/engagements/engagementsApi';

const INPUT =
  'w-full rounded-md border bg-white px-[13px] py-3 text-base text-ink shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] placeholder:text-[#6b7280] transition-colors focus:outline-none focus:ring-2';
const inputCls = (invalid?: string) =>
  [
    INPUT,
    invalid
      ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
      : 'border-line focus:border-brand focus:ring-brand/15',
  ].join(' ');

const STATUSES = ['Draft', 'Active', 'Closed'];

/** The assembly code embedded in an EC code (S-codes: first 3 `_`-segments); '' otherwise. */
function deriveAcCode(code: string): string {
  if (/^S\d/i.test(code)) {
    const segs = code.split('_');
    if (segs.length >= 3) return segs.slice(0, 3).join('_');
  }
  return '';
}

interface EngagementFormValues {
  name: string;
  engagementDate: string;
  areaCode: string;
  owner: number | '';
  ecLevelId: number | '';
  status: string;
  description: string;
}

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Engagement name is required'),
  // Either an Organization Level or an Election Commission Level is required.
  areaCode: Yup.string().test(
    'scope',
    'Select an Organization Level or Election Commission Level',
    function () {
      const { areaCode, ecLevelId } = this.parent as { areaCode?: string; ecLevelId?: number | '' };
      return Boolean(areaCode) || (ecLevelId !== '' && ecLevelId != null);
    },
  ),
  owner: Yup.mixed().test('required', 'Engagement owner is required', (v) => v !== '' && v != null),
  engagementDate: Yup.string().required('Date is required'),
});

function Card({ eyebrow, children }: { eyebrow: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-line-soft bg-white shadow-card">
      <div className="border-b border-[#f3f4f6] px-6 py-5">
        <span className="text-xs font-semibold uppercase tracking-[0.6px] text-[#6b7280]">{eyebrow}</span>
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

function Label({ children, required }: { children: ReactNode; required?: boolean }) {
  return (
    <span className="text-sm font-medium text-[#374151]">
      {children}
      {required && <span className="text-[#ef4444]"> *</span>}
    </span>
  );
}

export default function CreateEngagementPage() {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);

  const { data: editEngagement } = useGetEngagementQuery(editId!, { skip: !isEdit });
  // The signed-in user's org subtree — carries the org id used to scope the other lists.
  const { data: myTreeData } = useGetMyTreeQuery();
  const orgId = myTreeData?.organization ?? null;
  // Organization Level list comes from the org's full node tree
  // (GET /organisation/node/tree/?organization=<id>), scoped by the org my-tree returns.
  const { data: orgTreeData } = useGetNodeTreeQuery(
    { organization: orgId! },
    { skip: orgId == null },
  );
  const orgTree = useMemo(() => orgTreeData ?? [], [orgTreeData]);
  const dispatch = useAppDispatch();
  // Election Commission Level — the org's selected boundaries (GET /organisation/boundary/)
  // seed a lazy constituency tree: each boundary node drills down (AC → Rural/Urban → …)
  // via the step-2 territory APIs.
  const { data: boundaries } = useGetBoundariesQuery(
    { organization: orgId! },
    { skip: orgId == null },
  );
  const [ecSeeds, setEcSeeds] = useState<TreeNode[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const seeds = await Promise.all((boundaries ?? []).map((b) => resolveBoundarySeed(dispatch, b)));
      if (!cancelled) setEcSeeds(seeds.filter((s): s is TreeNode => s !== null));
    })();
    return () => {
      cancelled = true;
    };
  }, [boundaries, dispatch]);
  // Held here (not inside the dropdown) so the drilled path + selection survive re-opening.
  const [ecExpanded, setEcExpanded] = useState<Set<string>>(new Set());
  const [ecChildren, setEcChildren] = useState<Record<string, TreeNode[]>>({});
  // Display labels for the dropdown triggers.
  const [ecLabel, setEcLabel] = useState('');
  const [orgLabel, setOrgLabel] = useState('');
  // The Organization Level tree's selected key, and the selected EC node's assembly code.
  const [orgKey, setOrgKey] = useState('');
  const [ecAcCode, setEcAcCode] = useState('');

  const [createEngagement, { isLoading: isCreating }] = useCreateEngagementMutation();
  const [updateEngagement, { isLoading: isUpdating }] = useUpdateEngagementMutation();
  const saving = isCreating || isUpdating;

  const formik = useFormik<EngagementFormValues>({
    initialValues: {
      name: '',
      engagementDate: '',
      areaCode: '',
      owner: '',
      ecLevelId: '',
      status: 'Active',
      description: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      // Scopes are mutually exclusive — send both fields, nulling the unused one so an
      // update clears whatever the other scope had before.
      const usingOrgLevel = values.ecLevelId !== '';
      const payload: CreateEngagementPayload = {
        name: values.name.trim(),
        description: values.description.trim(),
        status: values.status,
        engagement_date: values.engagementDate,
        owner: Number(values.owner),
        ec_location_level_id: usingOrgLevel ? Number(values.ecLevelId) : null,
        area_code: usingOrgLevel ? null : values.areaCode.trim() || null,
        ...(usingOrgLevel && orgId != null ? { organization: orgId } : {}),
      };
      try {
        if (isEdit && editId) {
          await updateEngagement({ id: editId, patch: payload }).unwrap();
        } else {
          await createEngagement(payload).unwrap();
        }
        navigate('/user/engagements');
      } catch {
        /* surfaced via inline errors / retry */
      }
    },
  });

  const { values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched } = formik;

  const orgSelected = values.ecLevelId !== '';
  const ecSelected = values.areaCode.trim() !== '';

  // Owner options come from node-users, scoped by whichever level was chosen:
  //   • Organization Level → ?node=<node>&organization=<org>
  //   • Election Commission Level → ?area_code=<code>&ac_code=<assembly code>
  const nodeUsersArg = orgSelected
    ? { node: Number(values.ecLevelId), organization: orgId }
    : ecSelected
      ? { area_code: values.areaCode.trim(), ...(ecAcCode ? { ac_code: ecAcCode } : {}) }
      : null;
  const { data: owners } = useGetNodeUsersQuery(nodeUsersArg ?? { node: -1 }, {
    skip: nodeUsersArg == null,
  });

  // Selecting a constituency node — stores its area_code + assembly code (owners load by those).
  const selectEcNode = (n: TreeNode) => {
    if (!n.areaCode) return;
    setFieldValue('areaCode', n.areaCode);
    setEcAcCode(n.acCode ?? (n.kind === 'assembly' ? n.areaCode : ''));
    setFieldTouched('areaCode', true, false);
    setEcLabel(`${EC_LEVEL[n.kind]?.name ? `${EC_LEVEL[n.kind]!.name} · ` : ''}${n.label}`);
    setFieldValue('ecLevelId', '');
    setOrgKey('');
    setOrgLabel('');
    setFieldValue('owner', '');
  };

  // Selecting an Organization Level node — clears the Election Commission Level.
  const selectOrgLevel = (key: string, label: string) => {
    setOrgKey(key);
    setOrgLabel(label);
    setFieldValue('ecLevelId', keyId(key));
    setFieldTouched('ecLevelId', true, false);
    setFieldValue('areaCode', '');
    setEcAcCode('');
    setEcLabel('');
    setFieldValue('owner', '');
  };

  const clearOrgLevel = () => {
    setOrgKey('');
    setOrgLabel('');
    setFieldValue('ecLevelId', '');
    setFieldValue('owner', '');
  };
  const clearEcLevel = () => {
    setFieldValue('areaCode', '');
    setEcAcCode('');
    setEcLabel('');
    setFieldValue('owner', '');
  };

  // Edit mode: prefill from the loaded engagement.
  useEffect(() => {
    if (!editEngagement) return;
    const e = editEngagement as Engagement;
    setFieldValue('name', engName(e) === 'Untitled' ? '' : engName(e));
    setFieldValue('description', e.description ?? '');
    setFieldValue('status', e.status || 'Active');
    setFieldValue('engagementDate', e.engagement_date ?? '');
    setFieldValue('areaCode', engArea(e));
    if (engArea(e)) setEcLabel(engArea(e)); // refined to the boundary's name once it loads
    if (e.ec_location_level_id != null) setFieldValue('ecLevelId', e.ec_location_level_id);
    const ownerId = typeof e.owner === 'object' ? e.owner?.id : e.owner;
    if (ownerId != null) setFieldValue('owner', Number(ownerId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editEngagement]);

  // Edit mode: label the EC Level trigger from the saved area_code, and restore its
  // assembly code (from the boundary context, else derived from the code) for owners.
  useEffect(() => {
    if (!editEngagement) return;
    const code = engArea(editEngagement as Engagement);
    if (!code) return;
    const b = (boundaries ?? []).find((x) => x.area_code === code);
    if (b) setEcLabel(b.area_name ? `${b.area_name} · ${code}` : code);
    setEcAcCode(b?.context?.assembly?.code ?? deriveAcCode(code));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editEngagement, boundaries]);

  // Edit mode: label the Organization Level trigger from the saved node id.
  useEffect(() => {
    if (!editEngagement || !orgTree?.length) return;
    const rank = (editEngagement as Engagement).ec_location_level_id;
    if (rank == null) return;
    const k = nodeKey(Number(rank));
    const label = orgNodeLabels(orgTree)[k];
    if (label) {
      setOrgKey(k);
      setOrgLabel(label);
    }
  }, [editEngagement, orgTree]);

  const errorOf = (name: keyof EngagementFormValues) =>
    touched[name] && errors[name] ? (errors[name] as string) : undefined;

  return (
    <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-6" data-testid="create-engagement">
      {/* Heading */}
      <div className="flex flex-col gap-1">
        <Breadcrumbs
          items={[
            { label: 'Engagements', to: '/user/engagements' },
            { label: isEdit ? 'Edit Engagement' : 'Create Engagement' },
          ]}
        />
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Back to engagements"
            onClick={() => navigate('/user/engagements')}
            className="text-ink-900 transition-colors hover:text-brand"
          >
            <ArrowLeftIcon className="size-5" />
          </button>
          <h1 className="text-2xl font-semibold tracking-[-0.4px] text-ink">
            {isEdit ? 'Edit Engagement' : 'Create Engagement'}
          </h1>
        </div>
      </div>

      {/* Basic information */}
      <Card eyebrow="Basic Information">
        <div className="flex flex-col gap-6">
          <label className="flex flex-col gap-2">
            <Label required>Engagement Name</Label>
            <input
              name="name"
              placeholder="Enter engagement name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputCls(errorOf('name'))}
            />
            {errorOf('name') && <span className="text-xs font-medium text-[#ba1a1a]">{errorOf('name')}</span>}
          </label>

          {/* Organization Level + Election Commission Level (choose one) */}
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-6 sm:flex-row">
              <div className="flex flex-1 flex-col gap-2">
                <Label required>Organization Level</Label>
                <TreeDropdown
                  placeholder="Select organization level"
                  valueLabel={orgLabel}
                  invalid={Boolean(errorOf('areaCode'))}
                  disabled={ecSelected}
                  onClear={clearOrgLevel}
                  panelClassName="w-[440px]"
                >
                  {(close) => (
                    <OrgNodeTree
                      nodes={orgTree}
                      hideAreas
                      selected={orgKey ? new Set([orgKey]) : new Set()}
                      onToggle={(key, label) => {
                        // Clicking the already-selected node unselects it.
                        if (key === orgKey) clearOrgLevel();
                        else selectOrgLevel(key, label);
                        close();
                      }}
                    />
                  )}
                </TreeDropdown>
              </div>

              <div className="flex flex-1 flex-col gap-2">
                <Label required>Election Commission Level</Label>
                <TreeDropdown
                  placeholder="Select Election Commission level"
                  valueLabel={ecLabel}
                  invalid={Boolean(errorOf('areaCode'))}
                  disabled={orgSelected}
                  onClear={clearEcLevel}
                  panelClassName="w-[440px]"
                >
                  {(close) => (
                    <div className="max-h-[60vh] overflow-y-auto p-1">
                      <ConstituencyTreeSelect
                        roots={ecSeeds}
                        selectedAreaCode={values.areaCode || undefined}
                        onSelect={(n) => {
                          selectEcNode(n);
                          close();
                        }}
                        expanded={ecExpanded}
                        setExpanded={setEcExpanded}
                        childrenMap={ecChildren}
                        setChildrenMap={setEcChildren}
                      />
                    </div>
                  )}
                </TreeDropdown>
              </div>
            </div>
            {errorOf('areaCode') ? (
              <span className="text-xs font-medium text-[#ba1a1a]">{errorOf('areaCode')}</span>
            ) : (
              <span className="text-xs text-[#6b7280]">
                Note: choose <strong>either</strong> an Organization Level or an Election Commission
                Level — selecting one disables the other, and its owners load below.
              </span>
            )}
          </div>

          {/* Engagement Owner + Date */}
          <div className="flex flex-col gap-6 sm:flex-row">
            <div className="flex flex-1 flex-col gap-2">
              <Label required>Engagement Owner</Label>
              <Select
                className="w-full"
                placeholder={orgSelected || ecSelected ? 'Search and select owner' : 'Select a level first'}
                showSearch
                optionFilterProp="label"
                disabled={!orgSelected && !ecSelected}
                status={errorOf('owner') ? 'error' : undefined}
                prefix={<FieldOwnerIcon className="size-5 text-[#9ca3af]" />}
                value={values.owner === '' ? undefined : values.owner}
                onChange={(v) => setFieldValue('owner', v)}
                onBlur={() => setFieldTouched('owner', true)}
                options={(owners ?? []).map((u) => ({ value: u.id, label: areaUserName(u) }))}
              />
              {errorOf('owner') ? (
                <span className="text-xs font-medium text-[#ba1a1a]">{errorOf('owner')}</span>
              ) : (
                <span className="text-xs text-[#6b7280]">
                  Owners are the users under the selected level.
                </span>
              )}
            </div>

            <label className="flex flex-1 flex-col gap-2">
              <Label required>Date</Label>
              <DatePicker
                className="w-full"
                format="DD/MM/YYYY"
                placeholder="dd/mm/yyyy"
                status={errorOf('engagementDate') ? 'error' : undefined}
                value={values.engagementDate ? dayjs(values.engagementDate) : null}
                onChange={(d) => setFieldValue('engagementDate', d ? d.format('YYYY-MM-DD') : '')}
                onBlur={() => setFieldTouched('engagementDate', true)}
              />
              {errorOf('engagementDate') && (
                <span className="text-xs font-medium text-[#ba1a1a]">{errorOf('engagementDate')}</span>
              )}
            </label>
          </div>
        </div>
      </Card>

      {/* Engagement status */}
      <Card eyebrow="Engagement Status">
        <div className="flex flex-wrap items-center gap-8">
          {STATUSES.map((s) => {
            const active = values.status === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setFieldValue('status', s)}
                className="flex items-center gap-2"
                role="radio"
                aria-checked={active}
              >
                <span
                  className={[
                    'flex size-[18px] items-center justify-center rounded-full border transition-colors',
                    active ? 'border-[#003da5] bg-[#003da5]' : 'border-[#cbd5e1] bg-white',
                  ].join(' ')}
                >
                  {active && (
                    <span className="size-2.5 rounded-full bg-white shadow-[0px_5px_20px_0px_rgba(0,0,0,0.2)]" />
                  )}
                </span>
                <span className="text-sm text-[#374151]">{s}</span>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Description */}
      <Card eyebrow="Description">
        <label className="flex flex-col gap-2">
          <Label>Engagement Description</Label>
          <textarea
            name="description"
            rows={5}
            placeholder="Enter engagement description..."
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            className={[inputCls(), 'resize-none placeholder:text-[#9ca3af]'].join(' ')}
          />
        </label>
      </Card>

      {/* Actions */}
      <div className="flex flex-wrap justify-end gap-4">
        <button
          type="button"
          onClick={() => navigate('/user/engagements')}
          className="rounded-lg border border-brand px-8 py-3 text-base text-brand transition-colors hover:bg-brand-shade"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-2 rounded-lg bg-brand px-8 py-3 text-base text-white shadow-[0px_10px_15px_-3px_rgba(0,61,155,0.2),0px_4px_6px_-4px_rgba(0,61,155,0.2)] transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'Saving…' : isEdit ? 'Update Engagement' : 'Create Engagement'}
          <RocketIcon className="size-[15px]" />
        </button>
      </div>
    </form>
  );
}
